import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from './useSocket';
import { useTenantStore } from '@/stores/tenantStore';
import { useRestaurantSettings } from '@/features/restaurant/hooks/useRestaurantSettings';
import { useToast } from '@/hooks/use-toast';

const logOrderEventDebug = (message: string, details?: Record<string, unknown>): void => {
  if (!import.meta.env.DEV) {
    return;
  }
  if (details) {
    console.debug(`[useOrderEvents] ${message}`, details);
    return;
  }
  console.debug(`[useOrderEvents] ${message}`);
};

interface OrderCreatedEvent {
  id: string;
  code: string;
  status: string;
  paymentStatus: 'PAID' | 'UNPAID' | 'REFUNDED' | 'VOIDED';
  totalCents: number;
  tableNumber: string | null;
  placedAt: number;
  source: 'QR' | 'COUNTER';
  totals: {
    currency: string;
    subtotal_cents: number;
    tax_cents: number;
    service_cents: number;
    tip_cents: number;
    total_cents: number;
  };
  customerName: string | null;
  customerPhone: string | null;
}

interface OrderUpdatedEvent {
  id: string;
  status?: string;
  paymentStatus?: string;
  kdsPriority?: string;
  kdsPrepSeconds?: number | null;
  kdsPaused?: boolean;
  kdsPausedAt?: number | null;
  kdsWaiterNotifiedAt?: number;
}

interface QrScanRecordedEvent {
  tenantId?: string | null;
  tableId: string;
  tableNumber?: number | string | null;
  qrSlug?: string | null;
  timestamp: number;
  day?: string | null;
  timezone?: string | null;
}

export const useOrderEvents = (): void => {
  const { socket, connected } = useSocket();
  const queryClient = useQueryClient();
  const { activeRestaurantId } = useTenantStore();
  const { preferences } = useRestaurantSettings();
  const { toast } = useToast();

  useEffect(() => {
    if (!socket || !connected || !activeRestaurantId) {
      return;
    }

    const HERO_QUERY_PREFIXES: ReadonlyArray<ReadonlyArray<string>> = [
      ['dashboard-v3', 'orders'],
      ['dashboard-v3', 'tables'],
      ['dashboard-v3', 'table-activity'],
      ['dashboard-v3', 'payments'],
      ['dashboard-v3', 'sales'],
      ['dashboard-v3', 'qr-metrics'],
    ];

    const HEAVY_QUERY_PREFIXES: ReadonlyArray<ReadonlyArray<string>> = [
      ['dashboard-v3', 'items'],
    ];

    const heavyTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const invalidateQuery = (
      filters: Parameters<typeof queryClient.invalidateQueries>[0],
    ): void => {
      queryClient.invalidateQueries(filters).catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('[useOrderEvents] failed to invalidate queries', filters, error);
        }
      });
    };

    const invalidateByPrefix = (prefix: ReadonlyArray<string>) => {
      invalidateQuery({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          prefix.every((segment, index) => query.queryKey[index] === segment),
      });
    };

    const invalidateHeroQueries = () => {
      HERO_QUERY_PREFIXES.forEach(invalidateByPrefix);
    };

    const scheduleHeavyInvalidate = (prefix: ReadonlyArray<string>) => {
      const timerKey = prefix.join('|');
      // Skip debounce if we're on the server or already scheduled
      if (typeof window === 'undefined') {
        invalidateByPrefix(prefix);
        return;
      }

      if (heavyTimers.has(timerKey)) {
        return;
      }

      const timer = window.setTimeout(() => {
        heavyTimers.delete(timerKey);
        invalidateByPrefix(prefix);
      }, 1500);

      heavyTimers.set(timerKey, timer);
    };

    const scheduleHeavyQueries = (prefixes: ReadonlyArray<ReadonlyArray<string>> = HEAVY_QUERY_PREFIXES) => {
      prefixes.forEach(scheduleHeavyInvalidate);
    };

    const invalidateTablesAndActivity = () => {
      invalidateByPrefix(['dashboard-v3', 'tables']);
      invalidateByPrefix(['dashboard-v3', 'table-activity']);
      invalidateByPrefix(['tables']);
    };

    const handleDashboardTablesUpdated = () => {
      logOrderEventDebug('dashboard tables updated');
      invalidateTablesAndActivity();
    };

    const handleDashboardTableActivityUpdated = () => {
      logOrderEventDebug('dashboard table activity updated');
      invalidateTablesAndActivity();
    };

    const handleDashboardPaymentsUpdated = () => {
      logOrderEventDebug('dashboard payments updated');
      invalidateByPrefix(['dashboard-v3', 'payments']);
      invalidateByPrefix(['dashboard-v3', 'sales']);
      scheduleHeavyQueries();
    };

    const handleDashboardStaffUpdated = () => {
      logOrderEventDebug('dashboard staff updated');
      invalidateByPrefix(['dashboard-v3', 'staff']);
    };

    const handleDashboardReportsUpdated = () => {
      logOrderEventDebug('dashboard reports updated');
      invalidateByPrefix(['dashboard-v3', 'sales']);
      invalidateByPrefix(['reports']);
      scheduleHeavyQueries();
    };

    const handleOrderCreated = (event: OrderCreatedEvent) => {
      logOrderEventDebug('order created', {
        id: event.id,
        status: event.status,
        paymentStatus: event.paymentStatus,
        totalCents: event.totalCents,
        source: event.source,
      });
      
      // Invalidate relevant queries to trigger refetch
      invalidateQuery({ queryKey: ['orders', 'list'] });
      invalidateQuery({ queryKey: ['orders', 'recent'] });
      invalidateQuery({ queryKey: ['kds', 'orders'] });
      invalidateQuery({ queryKey: ['payments'] });
      invalidateQuery({ queryKey: ['customers'] });
      invalidateQuery({ queryKey: ['kpis', 'today'] });
      invalidateByPrefix(['reports']);
      invalidateTablesAndActivity();
      invalidateHeroQueries();
      scheduleHeavyQueries();

      if (preferences?.notifications ?? true) {
        const currencyCode = (event.totals.currency ?? 'USD').toUpperCase();
        const amountLabel = `${currencyCode} ${(event.totalCents / 100).toFixed(2)}`;
        const sourceLabel = event.source === 'QR' ? 'QR' : 'Counter';
        toast({
          title: 'New order received',
          description: `${sourceLabel} order ${event.code ?? event.id.slice(0, 6).toUpperCase()} • ${amountLabel}`,
        });
      }

      if ((preferences?.soundAlerts ?? true) && typeof window !== 'undefined') {
        const globalWindow = window as typeof window & { webkitAudioContext?: typeof AudioContext };
        const AudioCtor = globalWindow.AudioContext ?? globalWindow.webkitAudioContext;
        if (AudioCtor) {
          try {
            const context = new AudioCtor();
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            oscillator.type = 'sine';
            oscillator.frequency.value = 880;
            gain.gain.setValueAtTime(0.2, context.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.4);
            oscillator.connect(gain);
            gain.connect(context.destination);
            oscillator.start();
            oscillator.stop(context.currentTime + 0.5);
            oscillator.onended = () => {
              context.close().catch(() => undefined);
            };
          } catch (audioError) {
            console.warn('[useOrderEvents] failed to play notification tone', audioError);
          }
        }
      }
    };

    const handleOrderUpdated = (event: OrderUpdatedEvent) => {
      logOrderEventDebug('order updated', {
        id: event.id,
        status: event.status,
        paymentStatus: event.paymentStatus,
        kdsPriority: event.kdsPriority,
      });
      
      // Invalidate relevant queries to trigger refetch
      invalidateQuery({ queryKey: ['orders', 'list'] });
      invalidateQuery({ queryKey: ['orders', 'recent'] });
      invalidateQuery({ queryKey: ['kds', 'orders'] });
      invalidateQuery({ queryKey: ['kpis', 'today'] });
      invalidateTablesAndActivity();
      invalidateHeroQueries();
      scheduleHeavyQueries();
      
      const statusTriggersPayments = event.status === 'SERVED' || event.status === 'PICKED_UP' || event.status === 'COMPLETED';
      const paymentStatusProvided = typeof event.paymentStatus === 'string' && event.paymentStatus.length > 0;

      if (statusTriggersPayments || paymentStatusProvided) {
        invalidateQuery({ queryKey: ['payments'] });
        invalidateQuery({ queryKey: ['customers'] });
      }
    };

    const handleQrScanRecorded = (event: QrScanRecordedEvent) => {
      logOrderEventDebug('qr scan recorded', {
        tableId: event.tableId,
        tenantId: event.tenantId,
      });

      invalidateHeroQueries();
      scheduleHeavyQueries();
      invalidateQuery({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((segment) => {
            if (typeof segment !== 'string') {
              return false;
            }
            return segment === 'qr-metrics' || segment === 'qrMetrics';
          }),
      });
    };

    // Listen for order events and qr scan events
    socket.on('order.created', handleOrderCreated);
    socket.on('order.updated', handleOrderUpdated);
    socket.on('qr.scan.recorded', handleQrScanRecorded);
    socket.on('dashboard.tables.updated', handleDashboardTablesUpdated);
    socket.on('dashboard.table-activity.updated', handleDashboardTableActivityUpdated);
    socket.on('dashboard.payments.updated', handleDashboardPaymentsUpdated);
    socket.on('dashboard.staff.updated', handleDashboardStaffUpdated);
    socket.on('dashboard.reports.updated', handleDashboardReportsUpdated);

    // Cleanup listeners
    return () => {
      socket.off('order.created', handleOrderCreated);
      socket.off('order.updated', handleOrderUpdated);
      socket.off('qr.scan.recorded', handleQrScanRecorded);
      socket.off('dashboard.tables.updated', handleDashboardTablesUpdated);
      socket.off('dashboard.table-activity.updated', handleDashboardTableActivityUpdated);
      socket.off('dashboard.payments.updated', handleDashboardPaymentsUpdated);
      socket.off('dashboard.staff.updated', handleDashboardStaffUpdated);
      socket.off('dashboard.reports.updated', handleDashboardReportsUpdated);
      heavyTimers.forEach((timer) => {
        if (typeof window !== 'undefined') {
          window.clearTimeout(timer);
        } else {
          clearTimeout(timer);
        }
      });
      heavyTimers.clear();
    };
  }, [socket, connected, activeRestaurantId, queryClient, preferences, toast]);
};
